import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { ErrorFallback } from './error-fallback'
import { SuperuserBanner } from './primitives/SuperuserBanner'
import { AccountSettingsScreen } from './screens/AccountSettings'
import { FriendsScreen } from './screens/Friends'
import { GameDetailScreen } from './screens/GameDetail'
import { GroupCreateScreen } from './screens/GroupCreate'
import { GroupHomeScreen } from './screens/GroupHome'
import { GroupSettingsScreen } from './screens/GroupSettings'
import { GroupsScreen } from './screens/Groups'
import { HomeScreen } from './screens/Home'
import { InviteLandingScreen } from './screens/InviteLanding'
import { OtherProfileScreen } from './screens/OtherProfile'
import { PlayDetailScreen } from './screens/PlayDetail'
import { PlayEditScreen } from './screens/PlayEdit'
import { ProfileScreen } from './screens/Profile'
import { PublicSeasonScreen } from './screens/PublicSeason'
import { RecordScopeScreen } from './screens/RecordScope'
import { SeasonCreateScreen } from './screens/SeasonCreate'
import { SeasonDetailScreen } from './screens/SeasonDetail'
import { SeasonEditScreen } from './screens/SeasonEdit'
import { SignInScreen } from './screens/SignIn'
import { RecordConfirmStep } from './screens/record/Confirm'
import { RecordDetailsStep } from './screens/record/Details'
import { RecordGameStep } from './screens/record/Game'
import { GroupRecordPlayLayout, NoGroupRecordPlayLayout } from './screens/record/Layout'
import { RecordPlayersStep } from './screens/record/Players'
import { RecordResultsStep } from './screens/record/Results'
import { Sentry } from './sentry'

const rootRoute = createRootRoute({
  // Cap the app at a phone-shaped width and center it. Pure courtesy
  // for desktop viewers — the design is mobile-first and looks correct
  // at this width. On phones (max-width is wider than viewport) the
  // wrapper is invisible.
  component: () => (
    <div
      style={{
        maxWidth: 480,
        margin: '0 auto',
        minHeight: '100vh',
        boxShadow: '0 0 24px -8px rgba(60,40,20,0.08)',
      }}
    >
      <SuperuserBanner />
      <Outlet />
    </div>
  ),
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/home' })
  },
})

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  component: HomeScreen,
})

const groupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/groups',
  component: GroupsScreen,
})

const groupNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/group/new',
  component: GroupCreateScreen,
})

const groupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/group/$id',
  component: GroupHomeScreen,
})

const recordLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/group/$id/record',
  component: GroupRecordPlayLayout,
})

const recordIndexRoute = createRoute({
  getParentRoute: () => recordLayoutRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/group/$id/record/game', params: { id: params.id } })
  },
})

const recordGameRoute = createRoute({
  getParentRoute: () => recordLayoutRoute,
  path: 'game',
  component: RecordGameStep,
})
const recordPlayersRoute = createRoute({
  getParentRoute: () => recordLayoutRoute,
  path: 'players',
  component: RecordPlayersStep,
})
const recordResultsRoute = createRoute({
  getParentRoute: () => recordLayoutRoute,
  path: 'results',
  component: RecordResultsStep,
})
const recordDetailsRoute = createRoute({
  getParentRoute: () => recordLayoutRoute,
  path: 'details',
  component: RecordDetailsStep,
})
const recordConfirmRoute = createRoute({
  getParentRoute: () => recordLayoutRoute,
  path: 'confirm',
  component: RecordConfirmStep,
})

// No-group record-play flow.
const noGroupRecordLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/record',
  component: NoGroupRecordPlayLayout,
})
// The /record index is the scope picker — pick a group or just-friends,
// then drop into the matching wizard. (The no-group layout skips its
// wizard chrome for this screen; see record/Layout.tsx.)
const noGroupRecordIndexRoute = createRoute({
  getParentRoute: () => noGroupRecordLayoutRoute,
  path: '/',
  component: RecordScopeScreen,
})
const noGroupRecordGameRoute = createRoute({
  getParentRoute: () => noGroupRecordLayoutRoute,
  path: 'game',
  component: RecordGameStep,
})
const noGroupRecordPlayersRoute = createRoute({
  getParentRoute: () => noGroupRecordLayoutRoute,
  path: 'players',
  component: RecordPlayersStep,
})
const noGroupRecordResultsRoute = createRoute({
  getParentRoute: () => noGroupRecordLayoutRoute,
  path: 'results',
  component: RecordResultsStep,
})
const noGroupRecordDetailsRoute = createRoute({
  getParentRoute: () => noGroupRecordLayoutRoute,
  path: 'details',
  component: RecordDetailsStep,
})
const noGroupRecordConfirmRoute = createRoute({
  getParentRoute: () => noGroupRecordLayoutRoute,
  path: 'confirm',
  component: RecordConfirmStep,
})

const playRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/$id',
  component: PlayDetailScreen,
  // `recorded` is set by the record flow right after a save — it drives
  // the "just recorded" treatment on the play screen.
  validateSearch: (search: Record<string, unknown>): { recorded?: true } =>
    search.recorded === true || search.recorded === 'true' ? { recorded: true } : {},
})

const playEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/play/$id/edit',
  component: PlayEditScreen,
})

const meRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/me',
  component: ProfileScreen,
})

const friendsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/friends',
  component: FriendsScreen,
})

const gameDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/group/$id/game/$gameId',
  component: GameDetailScreen,
})

const groupSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/group/$id/settings',
  component: GroupSettingsScreen,
})

const otherProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/u/$handle',
  component: OtherProfileScreen,
})

const inviteLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InviteLandingScreen,
})

const seasonCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/group/$id/seasons/new',
  component: SeasonCreateScreen,
})

const seasonDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/season/$id',
  component: SeasonDetailScreen,
})

const seasonEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/season/$id/edit',
  component: SeasonEditScreen,
})

const publicSeasonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/season/$token',
  component: PublicSeasonScreen,
})

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInScreen,
})

const accountSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountSettingsScreen,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  homeRoute,
  groupsRoute,
  groupNewRoute,
  groupRoute,
  recordLayoutRoute.addChildren([
    recordIndexRoute,
    recordGameRoute,
    recordPlayersRoute,
    recordResultsRoute,
    recordDetailsRoute,
    recordConfirmRoute,
  ]),
  noGroupRecordLayoutRoute.addChildren([
    noGroupRecordIndexRoute,
    noGroupRecordGameRoute,
    noGroupRecordPlayersRoute,
    noGroupRecordResultsRoute,
    noGroupRecordDetailsRoute,
    noGroupRecordConfirmRoute,
  ]),
  playRoute,
  playEditRoute,
  meRoute,
  friendsRoute,
  gameDetailRoute,
  groupSettingsRoute,
  otherProfileRoute,
  inviteLandingRoute,
  seasonCreateRoute,
  seasonDetailRoute,
  seasonEditRoute,
  publicSeasonRoute,
  signInRoute,
  accountSettingsRoute,
])

// TanStack Router's errorComponent doesn't auto-report to Sentry. The ref
// dedupes against React StrictMode's double-effect in dev (in prod the
// dependency array is enough).
const RouteErrorFallback = ({ error }: { error: Error }) => {
  const reportedRef = useRef(false)
  useEffect(() => {
    if (reportedRef.current) return
    reportedRef.current = true
    Sentry.captureException(error)
  }, [error])
  return <ErrorFallback error={error} />
}

export const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteErrorFallback,
})
